import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineS, HeadlineXS } from '@sorare/core/src/atoms/typography';
import { range } from '@sorare/core/src/lib/arrays';

const GAP = 8;
const BEVEL = 5;

const getClipPath = (max: number) =>
  range(max)
    .map(i => {
      const width = `calc((100% - (${max} - 1) * ${GAP}px) / ${max})`;
      const offset = `calc(${i} * (${width} + ${GAP}px))`;
      return [
        `${offset} 100%`,
        `${offset} ${BEVEL}px`,
        `calc(${offset} + ${BEVEL}px) 0`,
        `calc(${offset} + ${width}) 0`,
        `calc(${offset} + ${width}) calc(100% - ${BEVEL}px)`,
        `calc(${offset} + ${width} - ${BEVEL}px) 100%`,
      ].join(',');
    })
    .join(', ');

const GoldenWheelProgress = styled.progress<{ max: number }>`
  appearance: none;
  border: none;
  width: 100%;
  height: 14px;
  border-radius: 0px;
  color: var(--c-yellow-700);

  /* Create a shape to show the progress as if it was N bevel shaped segments */
  clip-path: polygon(${({ max }) => getClipPath(max)});

  &[value] {
    &::-moz-progress-bar {
      background: linear-gradient(
        0deg,
        var(--c-yellow-700) 0%,
        var(--c-yellow-200) 100%
      );
    }
    &::-webkit-progress-value {
      background: linear-gradient(
        0deg,
        var(--c-yellow-700) 0%,
        var(--c-yellow-200) 100%
      );
    }
    &::-webkit-progress-bar {
      background: linear-gradient(
        00deg,
        var(--c-nd-100) 0%,
        var(--c-nd-100) 100%
      );
    }
  }
`;

type Props = { nb: number; cost: number };

export const GoldenTickets = ({ nb, cost }: Props) => {
  return (
    <Vertical center>
      <HeadlineXS color="var(--c-nd-500)">
        <FormattedMessage
          id="marketplace.wheel.goldenTickets.title"
          defaultMessage="Golden Spin"
        />{' '}
        <FontAwesomeIcon icon={faQuestionCircle} />
      </HeadlineXS>
      <HeadlineS>
        <FormattedMessage
          id="marketplace.wheel.goldenTickets.subtitle"
          defaultMessage="{remainingSpins, plural, =0 {Next spin} one {In 1 spin} other {In {remainingSpins} spins}}"
          values={{ remainingSpins: Math.max(0, cost - nb) }}
        />
      </HeadlineS>
      <GoldenWheelProgress value={nb} max={cost} />
    </Vertical>
  );
};
